import React, { Fragment } from 'react';
import { arrayOf, bool, string, number, oneOf, shape } from 'prop-types';
import classnames from 'classnames';
import Image from 'components/Image';
import PinItButton from 'components/PinItButton';
import { HAS_SAVE_IMAGE } from 'constants/site';
import { imageUrl } from 'util/images';
import { POST_COL_WIDTH } from 'constants/layout';
import { MQ_SMALL_ONLY, MQ_MEDIUM_ONLY, MQ_LARGE } from 'constants/mq';
import { getAffiliateLinkProps } from 'util/affiliates/amazon';
import LazyLoad from 'components/LazyLoad';
import ImageCaption from 'components/ImageCaption';
import NoscriptImageFallback from 'components/NoscriptImageFallback';
import ImagePlaceholder from 'components/ImagePlaceholder';
import SaveImageToolBar from 'components/SaveImageToolBar';

const MAX_POST_IMG_WIDTH = POST_COL_WIDTH; // px

// Start loading images before they are in view. Note, this should be a LOW
// value. Performance is more important than preventing placeholders from
// being visible.
const LAZY_LOAD_OFFSET = 50; // px

// CLEANUP: figure out better alt text
const DEFAULT_ALT = 'Post Image';

const DEFAULT_AUDIENCE = ['mobile', 'tablet', 'desktop'];

/**
 * returns an image with all of its sub-components: caption, link, pinterest,
 * prism, etc.
 */
function Figure(props) {
  const {
    url,
    format,
    originalWidth,
    originalHeight,
    resizeMode,
    hashedPublicId,
    aspectRatio: baseAspectRatio,
    maxWidth,
    alt: altText,
    caption,
    credit,
    href,
    post,
    isPinnable,
    sourceSizes,
    placement,
    hasLazyImage,
    audience: baseAudience,
    hasPrism,
    isAffiliate,
    variant,
    hideImageActions
  } = props;
  const { canonical } = post;

  const alt = altText || DEFAULT_ALT;
  const audience = baseAudience || DEFAULT_AUDIENCE;
  const aspectRatio = baseAspectRatio || originalWidth / originalHeight;

  const hasImageActions = !hideImageActions && HAS_SAVE_IMAGE;

  // CLEANUP: iron out what props we need from above
  const imgProps = {
    url,
    format,
    alt,
    maxSize: {
      width: maxWidth,
      aspectRatio
    },
    sourceSizes,
    renderOps: {
      resizeMode,
      ...(post && post.resourceId === '35783537' ? { quality: 10 } : undefined)
    },
    className: 'Figure__image--original'
  };

  const image = <Image {...imgProps} />;

  const content = (
    <Fragment>
      {href ? (
        <a
          className="Figure__image-link"
          {...(isAffiliate ? getAffiliateLinkProps(href) : { href })}
        >
          {image}
        </a>
      ) : (
        image
      )}
      {hasImageActions === false && isPinnable && canonical && (
        <PinItButton
          pageUrl={canonical}
          imageUrl={imageUrl(url, { width: 1000 })}
          description={alt}
          placement="inline"
        />
      )}
    </Fragment>
  );

  const figureClasses = classnames('Figure', {
    [`Figure--${variant}`]: variant
  });

  // CLEANUP: remove this once we have a way to hide prism on individual images
  const hidePrism = [
    'at/art/design/2019-12/january-cure-2020/Jan-Cure-IG-Stories_Week_1',
    'at/art/design/2019-12/january-cure-2020/Jan-Cure-2020-775px',
    'at/art/design/2020-02/love-letters/AT-LoveLetters-neighborhoods',
    'at/art/design/2020-03/MakeoverMadenss-logo.jpg',
    'at/art/design/2020-03/spring-cleaning-2020/spring-cleaning-early.jpg',
    'at/art/design/2020-03/perils-of-home-burnout/Perils-Home-Burnout-frames.png',
    'at/art/design/2020-03/perils-of-home-burnout/Perils-Home-Burnout-lightbulb.png',
    'at/art/design/2020-03/perils-of-home-burnout/Perils-Home-Burnout-bookshelf.png',
    'at/art/design/2020-03/spring-cleaning-2020/spring-cleaning-logo-transparent',
    'small%20cool/Logos/Small-Cool-contest-logo-horizontal_2x.jpg',
    'small%20cool/Logos/Small-Cool-experience-horizontal_2x-100.jpg',
    'small%20cool/Graphics/Small%20Cool%20Group.png',
    'small%20cool/Graphics/SC_Header.gif',
    'small%20cool/Logos/Small-Cool-experience-stacked_2x-100.jpg',
    'small%20cool/Logos/Small-Cool-contest-logo-stacked_2x.jpg',
    'small%20cool/Logos/SmallCool_Simple_3290x2194.png',
    'small%20cool/Graphics/small-cool-last-call.jpg',
    'small%20cool/Graphics/small-cool-resume-feed.jpg',
    'small%20cool/Graphics/small-cool-resume-story.jpg',
    'small%20cool/Graphics/small-cool-resume-story-final.jpg',
    'small%20cool/Graphics/small-cool-resume-feed-final.jpg',
    'small%20cool/Graphics/SMALLCOOL_FB_email_lead_300x200.option02.png',
    'small%20cool/Graphics/SMALLCOOL_FB_email_lead_600x400.option02.png',
    'small%20cool/Graphics/SMALLCOOL_FB_email_lead_600x414.option01.png',
    'small%20cool/Graphics/SMALLCOOL_CONTEST_instagram_4.5__080x1350.option01.png',
    'small%20cool/Graphics/SMALLCOOL_CONTEST_instagram_story_template_logo_orange.png',
    'small%20cool/Graphics/SMALLCOOL_FB_email_lead_600x414.option02.png',
    'small%20cool/Graphics/SMALLCOOL_CONTEST_instagram_story_template_logo_pink.png',
    'small%20cool/Graphics/SMALLCOOL_FB_Twitter_banner_option01.png',
    'small%20cool/Graphics/SMALLCOOL_FB_Twitter_banner_option02.png',
    'small%20cool/Graphics/SMALLCOOL_CONTEST_instagram_4.5__080x1350.option02.png',
    'small%20cool/Graphics/SMALLCOOL_CONTEST_instagram_story_template_orange.png',
    'small%20cool/Graphics/SMALLCOOL_CONTEST_instagram_story_template_logo_yellow.png',
    'small%20cool/Graphics/SMALLCOOL_CONTEST_instagram_story_template_logo_red.png',
    'small%20cool/Graphics/SMALLCOOL_homepage_bundle_2000x1000.png',
    'small%20cool/Graphics/SMALLCOOL_FB_email_lead_300x200.option01.png',
    'small%20cool/Graphics/SMALLCOOL_CONTEST_instagram_4.5__080x1350.option03.png',
    'small%20cool/Graphics/SMALLCOOL_FB_dedicated_email_1240x828.png',
    'small%20cool/Graphics/SMALLCOOL_CONTEST_share_image_3000x2000.png',
    'small%20cool/Graphics/SMALLCOOL_FB_editorial_image_3000x2000_option01.png',
    'small%20cool/Graphics/SMALLCOOL_FB_editorial_image_3000x2000.option02.png',
    'small%20cool/Graphics/SmallCoolContest_Email_Desktop2.png',
    'small%20cool/Graphics/SmallCoolContest_NavDesktop2.png',
    'small%20cool/Graphics/SmallCoolContest_NavMobile2.png',
    'small%20cool/Graphics/SmallCoolContest_EmailMobile2.png',
    'small%20cool/Graphics/SMALLCOOL_FB_email_lead_600x400.option01.png',
    'small%20cool/Graphics/SmallCoolContest-Desktop-Bordered.png',
    'small%20cool/Graphics/SmallCoolHomepage.png',
    'small%20cool/Graphics/SMALLCOOL_homepage_bundle_2000x1000.png',
    'small%20cool/Graphics/SMALLCOOL_editorial_share_3000x2000.option02.png'
  ].reduce((acc, curr) => (url && url.indexOf(curr) > -1 ? true : acc), false);

  return (
    <figure className={figureClasses}>
      <div className="Figure__image">
        {/**
         * NOTE we style the image via sibling selector, so the
         * PrismEntryButton needs to be _before_ the image.
         */}

        {hasLazyImage ? (
          <LazyLoad offset={LAZY_LOAD_OFFSET} once>
            {content}
          </LazyLoad>
        ) : (
          content
        )}

        <ImagePlaceholder
          aspectRatio={aspectRatio}
          cssHook="Figure"
          placement={placement}
        />

        {hasLazyImage && (
          <NoscriptImageFallback
            url={imageUrl(url, { width: MAX_POST_IMG_WIDTH })}
            alt={alt}
          />
        )}
      </div>
      {hasImageActions &&
        (placement === 'inPost' || placement === 'faqPage') && (
          <SaveImageToolBar
            post={post}
            isPinnable={isPinnable}
            hashedPublicId={hashedPublicId}
            hidePrism={hidePrism}
            hasPrism={hasPrism}
            imgProps={{ ...imgProps, className: 'Figure__image--save-modal' }}
          />
        )}

      {(caption || credit) && placement !== 'interrupter' && (
        <div className="Figure__image-info">
          <ImageCaption credit={credit} caption={caption} />
        </div>
      )}

      {/*
       * NOTE: we need to use a (fingers-crossed) innocuous CSS property/value
       * in this context ("empty-cells: show;") for the false clause -- instead
       * of an empty string -- to a prevent CSS parsing error.
       *
       * it seems that rework (the CSS parser used by amp-toolbox-optimizer)
       * doesn't consider a semicolon by itself to be valid, so it fails
       * parsing the CSS during the SeparateKeyframes step.
       */}
      <style jsx>{`
        @media ${MQ_SMALL_ONLY} {
          .Figure {
            ${!audience.includes('mobile')
              ? 'display: none;'
              : 'empty-cells: show;'}
          }
        }

        @media ${MQ_MEDIUM_ONLY} {
          .Figure {
            ${!audience.includes('tablet')
              ? 'display: none;'
              : 'empty-cells: show;'}
          }
        }

        @media ${MQ_LARGE} {
          .Figure {
            ${!audience.includes('desktop')
              ? 'display: none;'
              : 'empty-cells: show;'}
          }
        }
      `}</style>
    </figure>
  );
}

Figure.propTypes = {
  alt: string,
  caption: string,
  credit: string,
  format: string,
  // Where an image links to, if at all
  href: string,
  isPinnable: bool,
  aspectRatio: number,
  maxWidth: number,
  originalHeight: number,
  originalWidth: number,
  resizeMode: string,
  hashedPublicId: string,
  post: shape({ canonical: string }),
  // list of places where Figure might go
  placement: ImagePlaceholder.propTypes.placement,
  sourceSizes: Image.propTypes.sourceSizes,
  // The source of the image
  url: string.isRequired,
  hasLazyImage: bool,
  audience: arrayOf(oneOf(DEFAULT_AUDIENCE)),
  hasPrism: bool,
  imageItemIdx: number,
  isAffiliate: bool,
  variant: string,
  hideImageActions: bool
};

Figure.defaultProps = {
  alt: '',
  caption: undefined,
  credit: undefined,
  format: undefined,
  href: undefined,
  isPinnable: true, // Needs to be part of the item data (and false by default)
  aspectRatio: undefined,
  maxWidth: MAX_POST_IMG_WIDTH,
  originalHeight: undefined,
  originalWidth: undefined,
  resizeMode: 'scaleFit',
  hashedPublicId: undefined,
  placement: 'inPost',
  post: {
    canonical: undefined
  },
  sourceSizes: undefined,
  hasLazyImage: true,
  audience: DEFAULT_AUDIENCE,
  hasPrism: false,
  imageItemIdx: undefined,
  isAffiliate: false,
  variant: undefined,
  hideImageActions: false
};

export default Figure;
